import { alpha } from '@mui/material';

const theme = {
  colors: {
    white: '#FFFFFF',
    black: '#000000',
    primary: '#F89622', // Matches the orange accent seen in buttons and highlights
    secondary: '#66202D', // Matches the dark maroon used in buttons (e.g., "NEXT", "PAY NOW")
    text: '#1E1E1E', // Dark text color for most content
    gray: '#4A4A4A', // Lighter gray for secondary text (e.g., footer)
    border: '#ECECEC', // Light border color for cards
    disabled: '#AEB4AC', // Disabled state color
    success: '#2E7D32', // Green for "Yes" indicators (e.g., Regulatory: Yes)
    warning: '#FF9800', // Orange for pending states
    tab1: {
      background: '#FFF7D0', // Light yellow for "What Happens Next" tab
      border: '#DBB78D',
      icon: '#B99161',
      iconFill: '#FFFFFF',
    },
    tab2: {
      background: '#E5F6F6', // Light teal for "Service Selection" tab
      border: '#C6E0E0',
      icon: '#ECF8F8',
      iconFill: '#5D8383',
    },
    tab3: {
      background: '#E0EFFF', // Light blue for "Blocking" tab
      border: '#C6E0E0',
      icon: '#EAF4FF',
      iconFill: '#6D8DB0',
    },
  },
  fonts: {
    primary: "'PP Neue Montreal', 'Inter', sans-serif", // Primary font for headers and body
    secondary: "'Lato', sans-serif", // Secondary font for labels and buttons
    tertiary: "'LT Renovate', sans-serif", // Fallback font for specific sections
  },
  spacing: {
    xs: '8px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '40px',
  },
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '16px',
    pill: '50px', // Used for buttons like "NEXT" and "PAY NOW"
  },
};

export const DashboardStyles = {
  NavigationBar: {
    appBar: (currentView, heroImage, heroImage1) => ({
      backgroundImage: `url(${currentView === 'landingPage' ? heroImage : heroImage1})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      boxShadow: 'none',
      padding: { xs: '0 16px', md: '0 117px' },
      height: { xs: currentView === 'landingPage' ? '288px' : '208px', md: currentView === 'landingPage' ? '360px' : '260px' },
      zIndex: 1,
    }),
    toolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: { xs: '8px', md: '14px' },
      flexDirection: { xs: 'row', sm: 'row' },
    },
    logo: {
      width: { xs: '120px', md: '150px' }, // Adjusted to match the "nutrify today" logo size
      height: { xs: '40px', md: '50px' },
      objectFit: 'contain',
    },
    navButtonsContainer: {
      display: { xs: 'none', sm: 'flex' },
      gap: { xs: '10px', md: '20px' },
      position: { sm: 'absolute' },
      left: { sm: '50%' },
      transform: { sm: 'translateX(-50%)' },
      fontFamily: theme.fonts.primary,
    },
    mobileMenu: {
      display: { xs: 'flex', sm: 'none' },
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: alpha(theme.colors.black, 0.95),
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      padding: '16px',
      zIndex: 10,
      transition: 'all 0.3s ease-in-out',
      opacity: { xs: 1, sm: 0 },
      maxHeight: { xs: '300px', sm: '0' },
      overflow: 'hidden',
    },
    navButton: {
      color: theme.colors.white,
      textTransform: 'none',
      fontSize: { xs: '16px', md: '18px' },
      fontWeight: 400,
      lineHeight: '200%',
      padding: { xs: '8px 12px', md: '8px 16px' },
      width: { xs: '100%', sm: 'auto' },
      textAlign: { xs: 'center', sm: 'left' },
      '&:hover': {
        backgroundColor: alpha(theme.colors.white, 0.1),
      },
    },
    logoutButton: {
      color: theme.colors.white,
      textTransform: 'uppercase',
      fontSize: { xs: '14px', md: '16px' },
      fontWeight: 700,
      fontFamily: theme.fonts.primary,
      lineHeight: '200%',
      padding: { xs: '8px 12px', md: '8px 16px' },
      width: { xs: '100%', sm: 'auto' },
      textAlign: { xs: 'center', sm: 'right' },
      '&:hover': {
        backgroundColor: alpha(theme.colors.white, 0.1),
      },
    },
    heroContent: (currentView) => ({
      height: {
        xs: currentView === 'landingPage' ? 'calc(288px - 48px)' : 'calc(208px - 48px)',
        md: currentView === 'landingPage' ? 'calc(360px - 64px)' : 'calc(260px - 64px)',
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center', // Centered for "Book Label Claim 8" header
      justifyContent: 'center',
      color: theme.colors.white,
      textAlign: 'center',
      paddingLeft: { xs: '16px', md: 0 },
    }),
    viewTitle: {
      fontWeight: 500,
      fontSize: { xs: '32px', md: '48px' }, // Matches the "Book Label Claim 8" font size
      paddingbottam: '70px',
      lineHeight: '90%',
      fontFamily: theme.fonts.primary,
      textTransform: 'none',
      marginBottom: { xs: '20px', md: '30px' },
    },
  },

  Dashboard: {
    container: {
      fontFamily: theme.fonts.primary,
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: theme.colors.white,
    },
    contentBox: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.large,
      border: `1px solid ${theme.colors.border}`,
      boxShadow: '0px 14px 24px rgba(0, 0, 0, 0.05)',
      padding: { xs: '16px', md: '30px' },
      width: { xs: '100%', md: '1200px' }, // Adjusted to match the card width in images
      margin: '0 auto',
      height: 'fit-content',
      position: 'relative',
      zIndex: 2,
      
    },
    filterTextField: {
        '& .MuiInputBase-root': {
            borderRadius: '12px',
            background:' #FAFAFA',
            border: `1px solid ${theme.colors.border}`,
            border: '1px solid rgb(196, 204, 192)',
           

            color: theme.colors.text,
            fontWeight: 400,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
        },
        '& .MuiSelect-select': {
            padding: '12px 36px 12px 14px',
            color: theme.colors.text,
            fontWeight: 400,
            '&:focus': {
                backgroundColor: 'transparent',
            },
        },
        '& .Mui-disabled': {
            color: theme.colors.text,
            WebkitTextFillColor: theme.colors.text,
            opacity: 0.6,
        },
        '& .MuiSvgIcon-root': {
            color: theme.colors.text,
        },
    },
    tableHeader: {
      fontFamily: theme.fonts.secondary,
      fontSize: { xs: '14px', md: '16px' },
      fontWeight: 400,
      color: theme.colors.gray,
    },
    tableRow: {
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      backgroundColor: '#FAFAFA',
      border: `1px solid #C4CCC0`,
      borderRadius: theme.borderRadius.medium,
      padding: { xs: '8px', sm: '10px 0' },
      minHeight: { xs: 'auto', sm: '48px' },
      marginBottom: { xs: '8px', sm: '10px' },
    },
    tableCell: {
      fontFamily: theme.fonts.primary,
      fontSize: { xs: '14px', md: '16px' },
      fontWeight: 400,
      color: theme.colors.text,
      padding: { xs: '4px 0', sm: '4px 0' },
    },
    tableCellBold: {
      fontFamily: theme.fonts.primary,
      fontSize: { xs: '14px', md: '16px' },
      fontWeight: 500,
      color: theme.colors.text,
    },
    actionButton: {
      color: theme.colors.secondary,
      fontFamily: theme.fonts.primary,
      fontSize: { xs: '14px', md: '16px' },
      fontWeight: 500,
      lineHeight: '200%',
      textTransform: 'none',
      padding: 0,
      minWidth: 'auto',
    },
    orderButton: {
      backgroundColor: theme.colors.secondary,
      color: theme.colors.white,
      borderRadius: theme.borderRadius.pill,
      textTransform: 'none',
      padding: { xs: '8px 24px', md: '10px 30px' },
      fontFamily: theme.fonts.primary,
      fontSize: { xs: '14px', md: '16px' },
      '&:hover': {
        backgroundColor: '#551A25',
      },
    },
    statusText: (status) => ({
      color: status === 'completed' ? theme.colors.success : theme.colors.warning,
      fontFamily: theme.fonts.primary,
      fontSize: { xs: '14px', md: '16px' },
      whiteSpace: 'nowrap',
    }),
    footer: {
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: 'space-between',
      alignItems: 'center',
      px: { xs: 2, md: 15 },
      py: 3,
      backgroundColor: theme.colors.white,
      gap: { xs: '16px', sm: 0 },
    },
    footerText: {
      fontFamily: theme.fonts.primary,
      fontWeight: 400,
      fontSize: { xs: '14px', md: '18px' },
      color: theme.colors.gray,
      lineHeight: '200%',
    },
  },
};

export default DashboardStyles;